import { alpha } from '@mui/material/styles';
import { ICustomTheme } from '../types/ICustomTheme';

// Variáveis modernas para transições e animações
export const MODERN_TRANSITIONS = {
     // Transições específicas (evitando 'all' que causa transições indesejadas)
     smooth: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
     fast: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
     slow: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
     bounce: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
     elastic: 'transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
     
     // Transições específicas
     transform: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
     opacity: 'opacity 0.25s ease-in-out',
     boxShadow: 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
     background: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
     border: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Shadows modernas com glassmorphism
export const MODERN_SHADOWS = {
     glass: {
          light: '0 8px 32px rgba(31, 38, 135, 0.37)',
          dark: '0 8px 32px rgba(0, 0, 0, 0.5)',
     },
     elevation: {
          1: '0 2px 4px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
          2: '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.15)',
          3: '0 8px 16px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.2)',
          4: '0 16px 32px rgba(0, 0, 0, 0.2), 0 8px 16px rgba(0, 0, 0, 0.25)',
          5: '0 24px 48px rgba(0, 0, 0, 0.25), 0 12px 24px rgba(0, 0, 0, 0.3)',
     },
     glow: {
          primary: (color: string) => `0 0 20px ${alpha(color, 0.3)}, 0 0 40px ${alpha(color, 0.2)}`,
         secondary: (color: string) => `0 0 15px ${alpha(color, 0.4)}, 0 0 30px ${alpha(color, 0.25)}`,
     },
     inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
};

// Gradientes modernos
export const MODERN_GRADIENTS = {
     primary: (theme: ICustomTheme) => 
          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
     
     secondary: (theme: ICustomTheme) => 
          `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
     
     glass: (theme: ICustomTheme) => 
          `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
     
     subtle: (theme: ICustomTheme) => 
          `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
     
     rainbow: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
     
     warm: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
     
     cool: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
};

// Bordas e cantos modernos
export const MODERN_BORDER = {
     radius: {
          xs: '4px',
          sm: '6px',
          md: '8px',
          lg: '12px',
          xl: '16px',
          '2xl': '20px',
          '3xl': '24px',
          full: '9999px',
     },
     glass: (theme: ICustomTheme) => `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
     glow: (color: string) => `1px solid ${alpha(color, 0.3)}`,
};

// Backdrop filters modernos
export const MODERN_BACKDROP = {
     blur: {
          sm: 'blur(4px)',
          md: 'blur(8px)',
          lg: 'blur(12px)',
          xl: 'blur(16px)',
     },
     glass: 'blur(10px) saturate(180%)',
};

// Animações e keyframes modernos
export const MODERN_KEYFRAMES = {
     fadeIn: {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
     },
     slideUp: {
          '0%': { transform: 'translateY(100%)', opacity: 0 },
          '100%': { transform: 'translateY(0)', opacity: 1 },
     },
     scaleIn: {
          '0%': { transform: 'scale(0.9)', opacity: 0 },
          '100%': { transform: 'scale(1)', opacity: 1 },
     },
     float: {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-10px)' },
     },
     pulse: {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.7 },
     },
     shimmer: {
          '0%': { backgroundPosition: '-200px 0' },
          '100%': { backgroundPosition: 'calc(200px + 100%) 0' },
     },
     ripple: {
          '0%': { transform: 'scale(0)', opacity: 1 },
          '100%': { transform: 'scale(4)', opacity: 0 },
     },
     breathe: {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' },
     },
};

// Hover effects modernos
export const MODERN_HOVER_EFFECTS = {
     lift: {
          transform: 'translateY(-4px)',
          boxShadow: MODERN_SHADOWS.elevation[3],
     },
     
     scale: {
          transform: 'scale(1.05)',
     },
     
     glow: (color: string) => ({
          boxShadow: MODERN_SHADOWS.glow.primary(color),
     }),
     
     tilt: {
          transform: 'perspective(1000px) rotateX(5deg) rotateY(5deg)',
     },
     
     glass: (theme: ICustomTheme) => ({
          backdropFilter: MODERN_BACKDROP.glass,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          borderColor: alpha(theme.palette.text.primary, 0.2),
     }),
};

// Focus states modernos
export const MODERN_FOCUS_STATES = {
     ring: (color: string) => ({
          outline: 'none',
          boxShadow: `0 0 0 3px ${alpha(color, 0.3)}`,
     }),
     
     glow: (color: string) => ({
          outline: 'none',
          boxShadow: `0 0 0 2px ${alpha(color, 0.2)}, 0 0 20px ${alpha(color, 0.1)}`,
     }),
     
     underline: (color: string) => ({
          '&::after': {
               content: '""',
               position: 'absolute' as const,
               bottom: 0,
               left: 0,
               width: '100%',
               height: '2px',
               backgroundColor: color,
               transform: 'scaleX(1)',
               transition: MODERN_TRANSITIONS.fast,
          },
     }),
};

// Estados de loading modernos
export const MODERN_LOADING_STATES = {
     skeleton: (theme: ICustomTheme) => ({
          background: `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.8)} 25%, ${alpha(theme.palette.divider, 0.5)} 50%, ${alpha(theme.palette.background.paper, 0.8)} 75%)`,
          backgroundSize: '200px 100%',
          animation: 'shimmer 1.5s infinite',
     }),
     
     pulse: {
          animation: 'pulse 2s infinite',
     },
     
     breathe: {
          animation: 'breathe 3s ease-in-out infinite',
     },
};

export default {
     MODERN_TRANSITIONS,
     MODERN_SHADOWS,
     MODERN_GRADIENTS,
     MODERN_BORDER,
     MODERN_BACKDROP,
     MODERN_KEYFRAMES,
     MODERN_HOVER_EFFECTS,
     MODERN_FOCUS_STATES,
     MODERN_LOADING_STATES,
};
